import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './LoyaltyDashboard.css';

const LoyaltyDashboard = () => {
  const [loyaltyData, setLoyaltyData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [expiringPoints, setExpiringPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchLoyaltyData();
    fetchTransactions();
    fetchExpiringPoints();
  }, []);

  const fetchLoyaltyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/loyalty/balance', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLoyaltyData(response.data);
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
      toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/loyalty/transactions?limit=10', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const fetchExpiringPoints = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/loyalty/expiring', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpiringPoints(response.data.expiringPoints || []);
    } catch (error) {
      console.error('Error fetching expiring points:', error);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: '#cd7f32',
      silver: '#c0c0c0',
      gold: '#ffd700',
      platinum: '#e5e4e2'
    };
    return colors[tier] || '#6366f1';
  };

  const getTierIcon = (tier) => {
    const icons = {
      bronze: '🥉',
      silver: '🥈',
      gold: '🥇',
      platinum: '💎'
    };
    return icons[tier] || '⭐';
  };

  const getTransactionIcon = (type) => {
    const icons = {
      earned: '✅',
      redeemed: '🎁',
      expired: '⏰',
      adjusted: '⚙️',
      bonus: '🎉'
    };
    return icons[type] || '📝';
  };

  if (loading) {
    return (
      <div className="loyalty-dashboard">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your loyalty rewards...</p>
        </div>
      </div>
    );
  }

  if (!loyaltyData) {
    return (
      <div className="loyalty-dashboard">
        <div className="error-message">
          <p>Unable to load loyalty data. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="loyalty-dashboard">
      <div className="loyalty-header">
        <h1>💎 My Loyalty Rewards</h1>
        <p>Earn points, unlock tiers, and enjoy exclusive benefits!</p>
      </div>

      {/* Current Tier Card */}
      <div 
        className="tier-status-card"
        style={{ 
          background: `linear-gradient(135deg, ${getTierColor(loyaltyData.tier.current)} 0%, ${getTierColor(loyaltyData.tier.current)}dd 100%)`
        }}
      >
        <div className="tier-info">
          <div className="tier-badge">
            <span className="tier-icon">{getTierIcon(loyaltyData.tier.current)}</span>
            <h2>{loyaltyData.tier.current.toUpperCase()} MEMBER</h2>
          </div>
          <div className="points-info">
            <div className="points-main">
              <span className="points-value">{loyaltyData.points.available.toLocaleString()}</span>
              <span className="points-label">Available Points</span>
            </div>
            <div className="points-stats">
              <div className="stat">
                <span className="stat-value">{loyaltyData.points.total.toLocaleString()}</span>
                <span className="stat-label">Total Earned</span>
              </div>
              <div className="stat">
                <span className="stat-value">{loyaltyData.points.redeemed.toLocaleString()}</span>
                <span className="stat-label">Redeemed</span>
              </div>
            </div>
          </div>
        </div>

        {loyaltyData.tier.nextTier && (
          <div className="tier-progress">
            <div className="progress-info">
              <span>Next Tier: {loyaltyData.tier.nextTier.toUpperCase()}</span>
              <span>{loyaltyData.tier.pointsToNextTier.toLocaleString()} points to go</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ 
                  width: `${((loyaltyData.points.total / (loyaltyData.points.total + loyaltyData.tier.pointsToNextTier)) * 100)}%` 
                }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="loyalty-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transactions
        </button>
        <button 
          className={`tab ${activeTab === 'benefits' ? 'active' : ''}`}
          onClick={() => setActiveTab('benefits')}
        >
          Benefits
        </button>
        <button 
          className={`tab ${activeTab === 'tiers' ? 'active' : ''}`}
          onClick={() => setActiveTab('tiers')}
        >
          All Tiers
        </button>
      </div>

      {/* Tab Content */}
      <div className="loyalty-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Statistics */}
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">🛍️</div>
                <div className="stat-content">
                  <h3>{loyaltyData.statistics?.totalOrders || 0}</h3>
                  <p>Total Orders</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-content">
                  <h3>₹{(loyaltyData.statistics?.totalSpent || 0).toLocaleString()}</h3>
                  <p>Total Spent</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⭐</div>
                <div className="stat-content">
                  <h3>{loyaltyData.points.available}</h3>
                  <p>Points Balance</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎯</div>
                <div className="stat-content">
                  <h3>{loyaltyData.tier.pointsToNextTier || '—'}</h3>
                  <p>To Next Tier</p>
                </div>
              </div>
            </div>

            {/* Current Benefits */}
            <div className="benefits-section">
              <h3>🎁 Your Current Benefits</h3>
              <div className="benefits-list">
                {loyaltyData.tier.benefits.map((benefit, index) => (
                  <div key={index} className="benefit-item">
                    <span className="benefit-icon">✓</span>
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expiring Points Alert */}
            {expiringPoints.length > 0 && (
              <div className="expiring-alert">
                <div className="alert-icon">⚠️</div>
                <div className="alert-content">
                  <h4>Points Expiring Soon!</h4>
                  <p>
                    You have {expiringPoints.reduce((sum, ep) => sum + ep.points, 0)} points 
                    expiring in the next 30 days. Use them before they expire!
                  </p>
                </div>
              </div>
            )}

            {/* Recent Transactions */}
            <div className="recent-transactions">
              <h3>📊 Recent Activity</h3>
              <div className="transactions-list">
                {transactions.slice(0, 5).map((transaction, index) => (
                  <div key={index} className="transaction-item">
                    <span className="transaction-icon">
                      {getTransactionIcon(transaction.type)}
                    </span>
                    <div className="transaction-details">
                      <span className="transaction-reason">{transaction.reason}</span>
                      <span className="transaction-date">
                        {new Date(transaction.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`transaction-points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                      {transaction.points > 0 ? '+' : ''}{transaction.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="transactions-tab">
            <h3>📜 Transaction History</h3>
            <div className="transactions-list full">
              {transactions.map((transaction, index) => (
                <div key={index} className="transaction-item detailed">
                  <span className="transaction-icon">
                    {getTransactionIcon(transaction.type)}
                  </span>
                  <div className="transaction-details">
                    <span className="transaction-reason">{transaction.reason}</span>
                    <span className="transaction-meta">
                      <span className="transaction-type">{transaction.type.toUpperCase()}</span>
                      {' • '}
                      <span className="transaction-date">
                        {new Date(transaction.createdAt).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </span>
                  </div>
                  <span className={`transaction-points ${transaction.points > 0 ? 'positive' : 'negative'}`}>
                    {transaction.points > 0 ? '+' : ''}{transaction.points} pts
                  </span>
                </div>
              ))}
              {transactions.length === 0 && (
                <div className="empty-state">
                  <p>No transactions yet. Start shopping to earn points!</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'benefits' && (
          <div className="benefits-tab">
            <h3>🎁 Your Tier Benefits</h3>
            <div className="benefits-grid">
              {loyaltyData.tier.benefits.map((benefit, index) => (
                <div key={index} className="benefit-card">
                  <div className="benefit-check">✓</div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>

            <div className="earn-info">
              <h3>💡 How to Earn More Points</h3>
              <ul>
                <li>🛍️ Place orders and earn points on every purchase</li>
                <li>⭐ Write product reviews (coming soon)</li>
                <li>👥 Refer friends and get bonus points (coming soon)</li>
                <li>🎂 Get birthday bonus points automatically</li>
                <li>🎯 Upgrade to higher tiers for better multipliers</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'tiers' && (
          <div className="tiers-tab">
            <h3>🏆 Loyalty Tier System</h3>
            <div className="tiers-comparison">
              {[
                { 
                  name: 'Bronze', 
                  icon: '🥉', 
                  threshold: 0, 
                  multiplier: '1x',
                  color: '#cd7f32',
                  benefits: ['1 point per ₹100 spent', 'Birthday bonus: 50 points', 'Access to exclusive deals']
                },
                { 
                  name: 'Silver', 
                  icon: '🥈', 
                  threshold: 1000, 
                  multiplier: '1.5x',
                  color: '#c0c0c0',
                  benefits: ['1.5 points per ₹100 spent', 'Birthday bonus: 100 points', 'Free shipping on orders above ₹500', 'Priority email support']
                },
                { 
                  name: 'Gold', 
                  icon: '🥇', 
                  threshold: 5000, 
                  multiplier: '2x',
                  color: '#ffd700',
                  benefits: ['2 points per ₹100 spent', 'Birthday bonus: 200 points', 'Free shipping on all orders', 'Priority customer support', 'Early access to sales']
                },
                { 
                  name: 'Platinum', 
                  icon: '💎', 
                  threshold: 15000, 
                  multiplier: '3x',
                  color: '#e5e4e2',
                  benefits: ['3 points per ₹100 spent', 'Birthday bonus: 500 points', 'Free shipping on all orders', 'Priority customer support', 'Early access to sales', 'Exclusive deals', 'Personal shopping assistant']
                }
              ].map((tier, index) => (
                <div 
                  key={index} 
                  className={`tier-comparison-card ${loyaltyData.tier.current === tier.name.toLowerCase() ? 'current' : ''}`}
                  style={{ borderTopColor: tier.color }}
                >
                  <div className="tier-header" style={{ color: tier.color }}>
                    <span className="tier-icon-large">{tier.icon}</span>
                    <h4>{tier.name}</h4>
                    {loyaltyData.tier.current === tier.name.toLowerCase() && (
                      <span className="current-badge">Current</span>
                    )}
                  </div>
                  <div className="tier-threshold">
                    {tier.threshold.toLocaleString()}+ points
                  </div>
                  <div className="tier-multiplier">
                    {tier.multiplier} Points Earning
                  </div>
                  <div className="tier-benefits-list">
                    {tier.benefits.map((benefit, bIndex) => (
                      <div key={bIndex} className="tier-benefit">
                        <span>✓</span>
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoyaltyDashboard;
